import React, { useState } from 'react'
import {
  Heading,
  Text,
  Button,
  TextInput,
  FormControl,
  InlineLink
} from '@primer/react-brand'
import CenteredAuthLayout from '../components/CenteredAuthLayout'
import { validateRequired } from '../utilities/validateAuthForm'

const MIN_PASSWORD_LENGTH = 6
const NEW_REQUEST_PATH = '/users/password/new'

// Page 2 of the password-reset flow: set a new password from the emailed token.
// Renders the "needs a reset link" state when no token is present, posts to
// Devise's PasswordsController#update (JSON), and resolves into the "Password
// changed" or "link no longer valid" states from the server response.
const ResetPasswordPage = ({ resetPasswordToken, signInPath = '/sign-in', membershipPath, currentUser }) => {
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  // 'form' | 'success' | 'expired'
  const [view, setView] = useState('form')
  const [redirectTo, setRedirectTo] = useState('/monitoring')

  const clearError = (field) => {
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const requiredError = validateRequired([
      { value: password, message: "Password can't be blank" }
    ])
    if (requiredError) { setErrors({ password: requiredError.message }); return }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setErrors({ password: `Password is too short (minimum is ${MIN_PASSWORD_LENGTH} characters)` })
      return
    }

    if (password !== passwordConfirmation) {
      setErrors({ password_confirmation: "Password confirmation doesn't match Password" })
      return
    }

    setLoading(true)
    setErrors({})

    try {
      const response = await fetch('/users/password', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          user: {
            reset_password_token: resetPasswordToken,
            password,
            password_confirmation: passwordConfirmation
          }
        })
      })

      const data = await response.json()

      if (data.success) {
        setRedirectTo(data.redirect_to || '/monitoring')
        setView('success')
      } else if (data.errors?.reset_password_token) {
        setView('expired')
      } else {
        const serverErrors = {}
        if (data.errors?.password) serverErrors.password = data.errors.password[0]
        if (data.errors?.password_confirmation) serverErrors.password_confirmation = data.errors.password_confirmation[0]
        setErrors(Object.keys(serverErrors).length ? serverErrors : { password: 'Something went wrong. Please try again.' })
      }
    } catch (err) {
      setErrors({ password: 'Something went wrong. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  const ctaStyle = {
    marginTop: '18px',
    backgroundColor: 'var(--base-color-scale-blue-5)',
    borderColor: 'var(--base-color-scale-blue-5)',
    borderRadius: '6px'
  }

  const badge = (backgroundColor, icon) => (
    <div
      style={{
        width: '52px',
        height: '52px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '22px',
        marginLeft: 'auto',
        marginRight: 'auto',
        backgroundColor
      }}
    >
      {icon}
    </div>
  )

  const layout = (children) => (
    <CenteredAuthLayout membershipPath={membershipPath} signInPath={signInPath} currentUser={currentUser}>
      {children}
    </CenteredAuthLayout>
  )

  // No token in the URL — the page was opened directly, not from a reset email.
  if (!resetPasswordToken) {
    return layout(
      <>
        {badge('var(--base-color-scale-gray-0)', (
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M5 11V8a7 7 0 0 1 14 0v3" stroke="var(--base-color-scale-gray-6)" strokeWidth="2" strokeLinecap="round" />
            <rect x="4" y="11" width="16" height="9" rx="2" stroke="var(--base-color-scale-gray-6)" strokeWidth="2" />
          </svg>
        ))}
        <Heading as="h1" size="5">This page needs a reset link</Heading>
        <Text size="200" variant="muted" as="div" style={{ marginTop: '14px' }}>
          You can't access this page without coming from a password reset email. If you did come from one, please make sure you used the full URL provided in the email.
        </Text>
        <Button as="a" href={NEW_REQUEST_PATH} variant="primary" block={true} style={ctaStyle}>
          Request a password reset
        </Button>
        <Text size="200" variant="muted" as="div" style={{ marginTop: '16px' }}>
          <InlineLink href={signInPath}>Return to sign in</InlineLink>
        </Text>
      </>
    )
  }

  // Token was rejected on submit (expired or already used).
  if (view === 'expired') {
    return layout(
      <>
        {badge('var(--base-color-scale-yellow-0)', (
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 4.5 21 19.5H3L12 4.5z" stroke="var(--base-color-scale-yellow-5)" strokeWidth="2" strokeLinejoin="round" />
            <path d="M12 10v4" stroke="var(--base-color-scale-yellow-5)" strokeWidth="2" strokeLinecap="round" />
            <circle cx="12" cy="17" r="0.6" fill="var(--base-color-scale-yellow-5)" stroke="var(--base-color-scale-yellow-5)" strokeWidth="1.1" />
          </svg>
        ))}
        <Heading as="h1" size="5">This link is no longer valid</Heading>
        <Text size="200" variant="muted" as="div" style={{ marginTop: '14px' }}>
          For your security, password reset links expire after a short time. This one has expired or already been used. Request a new link and we'll email you a fresh one.
        </Text>
        <Button as="a" href={NEW_REQUEST_PATH} variant="primary" block={true} style={ctaStyle}>
          Request a new link
        </Button>
        <Text size="200" variant="muted" as="div" style={{ marginTop: '16px' }}>
          <InlineLink href={signInPath}>Return to sign in</InlineLink>
        </Text>
      </>
    )
  }

  // Password successfully changed — the user is already signed in server-side.
  if (view === 'success') {
    return layout(
      <>
        {badge('var(--base-color-scale-green-0)', (
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M20 6.5 9.5 17 4.5 12" stroke="var(--base-color-scale-green-5)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ))}
        <Heading as="h1" size="5">Password changed</Heading>
        <Text size="200" variant="muted" as="div" style={{ marginTop: '14px' }}>
          Your password has been changed successfully. You're now signed in.
        </Text>
        <Button
          as="a"
          href={redirectTo}
          variant="primary"
          block={true}
          style={{
            marginTop: '18px',
            backgroundColor: 'var(--base-color-scale-gray-9)',
            borderColor: 'var(--base-color-scale-gray-9)',
            borderRadius: '6px'
          }}
        >
          Continue to CivicLens
        </Button>
      </>
    )
  }

  // Default — the set-a-new-password form.
  return layout(
    <>
      <Heading as="h1" size="5">Change your password</Heading>
      <Text size="200" variant="muted" as="div" style={{ marginTop: '14px' }}>
        You can change your password here. Enter the new password you'd like to use and confirm it to log back into CivicLens.
      </Text>

      <form onSubmit={handleSubmit} style={{ marginTop: '28px' }}>
        <FormControl fullWidth validationStatus={errors.password ? 'error' : undefined}>
          <FormControl.Label>Password</FormControl.Label>
          <TextInput
            name="password"
            type="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); clearError('password') }}
            placeholder="Password"
            autoFocus
          />
          {errors.password && (
            <FormControl.Validation validationStatus="error">{errors.password}</FormControl.Validation>
          )}
        </FormControl>
        {!errors.password && (
          <Text size="100" variant="muted" as="div" style={{ marginTop: '7px' }}>
            Minimum {MIN_PASSWORD_LENGTH} characters.
          </Text>
        )}

        <FormControl fullWidth validationStatus={errors.password_confirmation ? 'error' : undefined} style={{ marginTop: '22px' }}>
          <FormControl.Label>Confirm New Password</FormControl.Label>
          <TextInput
            name="password_confirmation"
            type="password"
            value={passwordConfirmation}
            onChange={(e) => { setPasswordConfirmation(e.target.value); clearError('password_confirmation') }}
            placeholder="Confirm New Password"
          />
          {errors.password_confirmation && (
            <FormControl.Validation validationStatus="error">{errors.password_confirmation}</FormControl.Validation>
          )}
        </FormControl>

        <Button
          type="submit"
          variant="primary"
          block={true}
          disabled={loading}
          style={ctaStyle}
        >
          {loading ? 'Changing...' : 'Change my password'}
        </Button>
      </form>
    </>
  )
}

export default ResetPasswordPage
